/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { BundleType } from '#resources/nls/bundle';
import type { Keys } from '#utils/UNSAFE_keys';
import type { SelectionDetail } from '#UNSAFE_Collection';
import type { CurrentRowVariant, TableSelectionDetail } from '#UNSAFE_TableView';
import type { TableColumn } from './Table';

import { memo } from 'preact/compat';
import { useInteractionStyle } from '#hooks/UNSAFE_useInteractionStyle';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { classNames } from '#utils/UNSAFE_classNames';
import { containsKey, isSameKey } from '#utils/UNSAFE_keys';
import { Selector } from '#UNSAFE_Selector';
import { tableRowStyles, tableRowStylesHC } from './themes/TableRowStyles.css';
import { TableCell } from './TableCell';

/**
 * Props for the TableRow Component
 */
type TableRowProps<K extends string | number, D, C extends string> = {
  tableId: string;
  rowKey: K;
  rowData: D;
  rowIndex: number;
  isFinalRow: boolean;
  columnsArray: TableColumn<K, D, C>[];
  rowHeaderColumnKeys: Set<C>;
  hasVerticalGridlines: boolean;
  hasHorizontalGridlines: boolean;
  hasFooters: boolean;
  hasVerticalUnderflow: boolean;
  hasMore: boolean;
  isSelected: boolean;
  isPreviousSelected: boolean;
  isRowSelectionEnabled: boolean;
  isSingleRowSelectionEnabled: boolean;
  isRtl: boolean;
  isHighContrast: boolean;
  selectedRowKeys?: Keys<K>;
  isShowFocusRing: boolean;
  activeColumnKey?: C;
  isTabbableMode: boolean;
  selectedColumnKeys?: Keys<C>;
  variant?: CurrentRowVariant;
  onSelectionChange?: (detail: TableSelectionDetail<K, C>, isExtendableSelection: boolean) => void;
  startStickyEdge?: number;
  endStickyEdge?: number;
  firstStickyColumnIndex?: number;
  finalStickyColumnIndex?: number;
  columnBorderEdges: { hasStart: boolean; hasEnd: boolean; width: number }[];
  disabledInteractionKeys?: ('enter' | 'space')[];
};

/**
 * The internal component used to render a single row in Table.
 */
function TableRow<K extends string | number, D, C extends string>({
  tableId,
  rowKey,
  rowData,
  rowIndex,
  isFinalRow,
  columnsArray,
  rowHeaderColumnKeys,
  hasVerticalGridlines,
  hasHorizontalGridlines,
  hasFooters,
  hasVerticalUnderflow,
  hasMore,
  isSelected,
  isPreviousSelected,
  isRowSelectionEnabled,
  isSingleRowSelectionEnabled,
  selectedRowKeys = { all: false, keys: new Set<K>() },
  isShowFocusRing,
  activeColumnKey,
  isTabbableMode,
  selectedColumnKeys = { all: false, keys: new Set<C>() },
  variant,
  disabledInteractionKeys,
  onSelectionChange,
  startStickyEdge,
  endStickyEdge,
  firstStickyColumnIndex,
  finalStickyColumnIndex,
  columnBorderEdges,
  isRtl,
  isHighContrast
}: TableRowProps<K, D, C>) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const { interactionProps, applyActiveStyle, applyHoverStyle, applyPseudoHoverStyle } =
    useInteractionStyle({ disabledKeys: disabledInteractionKeys });

  const rowStyles = isHighContrast ? tableRowStylesHC : tableRowStyles;
  const classArray = [rowStyles.base, isFinalRow ? rowStyles.lastRowHeight : rowStyles.rowHeight];
  const classes = classNames(classArray);

  const accessibleRowName = (rowIndex + 2).toString();
  const handleRowSelectorChange = (detail: SelectionDetail<K>) => {
    if (onSelectionChange != null) {
      onSelectionChange(
        {
          value: {
            row: detail.value,
            column: { all: false, keys: new Set<C>() }
          },
          target: detail.target
        },
        false
      );
    }
  };
  const selectorRenderer =
    isRowSelectionEnabled && !isSingleRowSelectionEnabled
      ? () => {
          // conversion from 0-based index and adding the header row as it is always rendered
          return (
            <Selector
              aria-label={translations.collection_selectRow({ ROW_NAME: accessibleRowName })}
              onChange={handleRowSelectorChange}
              rowKey={rowKey}
              selectedKeys={selectedRowKeys}
            />
          );
        }
      : undefined;

  let isPreviousColumnSelected = false;
  let isPreviousStickyColumnSelected = false;
  return (
    <tr
      aria-rowindex={rowIndex + 2}
      class={classes}
      role={'row'}
      key={rowKey}
      data-oj-key={rowKey}
      data-oj-table-row={tableId}
      {...(isRowSelectionEnabled
        ? isSelected
          ? { 'aria-selected': true }
          : { 'aria-selected': false }
        : {})}
      {...(typeof rowKey === 'number' && { 'data-oj-key-type': 'number' })}
      {...(isRowSelectionEnabled && interactionProps)}>
      {columnsArray.map((column: TableColumn<K, D, C>, columnIndex: number) => {
        const columnKey = column.key;
        const isColumnSelected = containsKey(selectedColumnKeys, columnKey);
        const isActive = columnKey === activeColumnKey;
        const hasTabIndex = isActive && !isTabbableMode;
        const isSticky = column.value.sticky === 'enabled';
        let nextVisibleColumn;
        let isNextVisibleColumnSelected = false;
        let isPreviousVisibleColumnSelected = false;
        let isFirstLogicalColumnIndex = false;
        let isFinalLogicalColumnIndex = false;
        if (isSticky) {
          if (startStickyEdge != null && firstStickyColumnIndex === columnIndex) {
            isFirstLogicalColumnIndex = true;
          }
          if (endStickyEdge != null && finalStickyColumnIndex === columnIndex) {
            isFinalLogicalColumnIndex = true;
          }
          if (
            (startStickyEdge != null && startStickyEdge > columnIndex) ||
            (endStickyEdge != null && endStickyEdge <= columnIndex)
          ) {
            for (let i = columnIndex + 1; i < columnsArray.length; i++) {
              if (columnsArray[i].value.sticky === 'enabled') {
                nextVisibleColumn = columnsArray[i];
                break;
              }
            }
          } else {
            nextVisibleColumn =
              columnIndex < columnsArray.length - 1 ? columnsArray[columnIndex + 1] : undefined;
          }
          isNextVisibleColumnSelected =
            startStickyEdge === columnIndex
              ? false
              : nextVisibleColumn != null && containsKey(selectedColumnKeys, nextVisibleColumn.key);
          if (
            (startStickyEdge != null && startStickyEdge >= columnIndex) ||
            (endStickyEdge != null && endStickyEdge < columnIndex)
          ) {
            isPreviousVisibleColumnSelected = isPreviousStickyColumnSelected;
          } else if (endStickyEdge !== columnIndex) {
            isPreviousVisibleColumnSelected = isPreviousColumnSelected;
          }
        } else {
          isPreviousVisibleColumnSelected = isPreviousColumnSelected;
        }
        const cellProps = {
          tableId: tableId,
          rowData: rowData,
          field: column.value.field,
          stickyColumn: column.value.sticky,
          columnKey: columnKey,
          columnIndex: columnIndex,
          rowKey: rowKey,
          rowIndex: rowIndex,
          isFinalRowIndex: isFinalRow,
          isFinalColumnIndex: columnIndex === columnsArray.length - 1,
          isFirstLogicalColumnIndex: isFirstLogicalColumnIndex,
          isFinalLogicalColumnIndex: isFinalLogicalColumnIndex,
          hasVerticalGridlines: hasVerticalGridlines,
          hasHorizontalGridlines: hasHorizontalGridlines,
          hasFooters: hasFooters,
          hasVerticalUnderflow: hasVerticalUnderflow,
          hasMore: hasMore,
          isRowSelected: isSelected,
          isPreviousRowSelected: isPreviousSelected,
          isSticky: isSticky,
          isColumnSelected: isColumnSelected,
          isNextColumnSelected: isNextVisibleColumnSelected,
          isPreviousColumnSelected: isPreviousVisibleColumnSelected,
          isShowFocusRing: isShowFocusRing && isActive,
          hasTabIndex: hasTabIndex,
          isTabbableMode: isTabbableMode,
          isAccRowHeader: rowHeaderColumnKeys.has(column.key),
          renderer: column.value.renderer,
          selectorRenderer: selectorRenderer,
          isRowSelectionEnabled: isRowSelectionEnabled,
          variant: variant,
          isRowSingleSelection: isSingleRowSelectionEnabled,
          applyActiveStyle: applyActiveStyle,
          applyHoverStyle: applyHoverStyle,
          applyPseudoHoverStyle: applyPseudoHoverStyle,
          isStickyStartEdge: columnIndex === startStickyEdge,
          isStickyEndEdge: columnIndex === endStickyEdge,
          hasStartEdge: columnBorderEdges[columnIndex].hasStart,
          hasEndEdge: columnBorderEdges[columnIndex].hasEnd,
          isRtl: isRtl,
          isHighContrast: isHighContrast,
          padding: column.value.padding,
          tooltip: column.value.tooltip,
          focusTarget: column.value.focusTarget,
          horizontalAlignment:
            column.value.alignment?.horizontal != null
              ? column.value.alignment.horizontal
              : column.value.horizontalAlignment,
          verticalAlignment: column.value.alignment?.vertical
        };
        isPreviousColumnSelected = isColumnSelected;
        if (isSticky) {
          isPreviousStickyColumnSelected = isColumnSelected;
        }
        return <TableCell {...cellProps} />;
      })}
    </tr>
  );
}

const _areStringSetsEquivalent = (set1?: Set<string>, set2?: Set<string>) => {
  if (set1 == null) {
    return set2 == null;
  }
  if (set2 == null) {
    return false;
  }
  return set1.size === set2.size && [...set1].every((x) => set2.has(x));
};

const _areColumnsArraysEquivalent = (
  columnsArray1: TableColumn<string | number, unknown, string>[],
  columnsArray2: TableColumn<string | number, unknown, string>[]
) => {
  if (columnsArray1.length === columnsArray2.length) {
    for (let i = 0; i < columnsArray1.length; i++) {
      if (
        columnsArray1[i].key !== columnsArray2[i].key ||
        columnsArray1[i].value !== columnsArray2[i].value
      ) {
        return false;
      }
    }
    return true;
  }
  return false;
};

export const MemoizeTableRow = memo(
  TableRow,
  (
    prev: TableRowProps<string | number, unknown, string>,
    next: TableRowProps<string | number, unknown, string>
  ) => {
    if (prev && next) {
      return (
        prev.tableId === next.tableId &&
        prev.rowKey === next.rowKey &&
        prev.rowData === next.rowData &&
        prev.rowIndex === next.rowIndex &&
        prev.isFinalRow === next.isFinalRow &&
        _areColumnsArraysEquivalent(prev.columnsArray, next.columnsArray) &&
        prev.hasHorizontalGridlines === next.hasHorizontalGridlines &&
        prev.hasVerticalGridlines === next.hasVerticalGridlines &&
        (!next.isFinalRow ||
          (prev.hasFooters === next.hasFooters &&
            prev.hasVerticalUnderflow === next.hasVerticalUnderflow &&
            prev.hasMore === next.hasMore)) &&
        _areStringSetsEquivalent(prev.rowHeaderColumnKeys, next.rowHeaderColumnKeys) &&
        prev.isSelected === next.isSelected &&
        prev.isPreviousSelected === next.isPreviousSelected &&
        prev.isRowSelectionEnabled === next.isRowSelectionEnabled &&
        prev.isSingleRowSelectionEnabled === next.isSingleRowSelectionEnabled &&
        (prev.isRowSelectionEnabled && !prev.isSingleRowSelectionEnabled
          ? isSameKey(prev.selectedRowKeys, next.selectedRowKeys)
          : true) &&
        isSameKey(prev.selectedColumnKeys, next.selectedColumnKeys) &&
        prev.isShowFocusRing === next.isShowFocusRing &&
        prev.activeColumnKey === next.activeColumnKey &&
        prev.isTabbableMode === next.isTabbableMode &&
        prev.variant === next.variant &&
        prev.onSelectionChange === next.onSelectionChange &&
        prev.startStickyEdge === next.startStickyEdge &&
        prev.endStickyEdge === next.endStickyEdge &&
        prev.firstStickyColumnIndex === next.firstStickyColumnIndex &&
        prev.finalStickyColumnIndex === next.finalStickyColumnIndex &&
        prev.columnBorderEdges === next.columnBorderEdges &&
        prev.isRtl === next.isRtl &&
        prev.isHighContrast === next.isHighContrast &&
        prev.disabledInteractionKeys === next.disabledInteractionKeys
      );
    }
    return false;
  }
);
